/**
 * Quoted vs actual (EW-8): how a figure the API computed is written. Nothing
 * here computes a figure — the variance, the % and every rate come from the
 * API. Absent renders `—`, never 0.
 */
import React from 'react';
import { fmtMoney, fmtNumber } from '../../../lib/earthworks';

const UNIT_WORD: Record<string, [one: string, many: string]> = {
  hour: ['h', 'h'],
  day: ['day', 'days'],
  load: ['load', 'loads'],
  m3: ['m³', 'm³'],
  yd3: ['yd³', 'yd³'],
};

/** `12 h`, `3 loads`, `120 m³`, money with its currency; absent → `—`. */
export function fmtMetric(n: number | null | undefined, unit: string, currency: string | null): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (unit === 'currency') return fmtMoney(n, currency);
  const w = UNIT_WORD[unit];
  return w ? `${fmtNumber(n)} ${n === 1 ? w[0] : w[1]}` : fmtNumber(n);
}

/** A rate the API computed (h per m³, loads per m³). */
export function fmtRate(n: number | null | undefined, per: string): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${fmtNumber(n)} ${per}`;
}

export const volumeWord = (units: 'metric' | 'imperial') => (units === 'imperial' ? 'yd³' : 'm³');

/** The API's variance %, signed, amber when actual ran over, green under; absent → `—`. */
export function VariancePct({ pct, testId }: { pct: number | null | undefined; testId?: string }) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return <span data-testid={testId} className="text-fieldiq-muted">—</span>;
  const tone = pct > 0 ? 'text-fi-amber' : pct < 0 ? 'text-fi-green' : 'text-fieldiq-text';
  return (
    <span data-testid={testId} className={`font-mono font-bold tabular-nums ${tone}`}>
      {pct > 0 ? '+' : ''}{fmtNumber(pct)}%
    </span>
  );
}

export function QvaNotes({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return (
    <ul className="space-y-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-muted" data-testid="ew-qva-notes">
      {notes.map((n) => <li key={n}>{n}</li>)}
    </ul>
  );
}

/** A labelled figure pair: quoted → actual. */
export function QvaPairStat({ label, quoted, actual }: { label: string; quoted: React.ReactNode; actual: React.ReactNode }) {
  return (
    <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
      <p className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</p>
      <p className="mt-1 text-sm text-fieldiq-text">
        <span className="text-fieldiq-muted">Quoted </span><span className="font-mono">{quoted}</span>
        <span className="text-fieldiq-muted"> · Actual </span><span className="font-mono font-bold">{actual}</span>
      </p>
    </div>
  );
}
